import React, { useEffect, useState } from "react";
import {
  fetchInsurancePlans,
  updateInsurancePlan,
  createInsurancePlan,
  deleteInsurancePlan,
} from "../APIs/InsurancePlanApi";

function InsurancePlanTab() {
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    id: null,
    planId: "",
    planName: "",
    planTypes: [{ typeName: "" }],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  async function loadPlans() {
    try {
      setLoading(true);
      const data = await fetchInsurancePlans();
      setPlans(data);
    } catch (err) {
      console.error("Failed to fetch plans", err);
      alert("Failed to load insurance plans.");
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleTypeChange(index, value) {
    const updatedTypes = [...form.planTypes];
    updatedTypes[index].typeName = value;
    setForm((prev) => ({ ...prev, planTypes: updatedTypes }));
  }

  function addTypeField() {
    setForm((prev) => ({
      ...prev,
      planTypes: [...prev.planTypes, { typeName: "" }],
    }));
  }

  function removeTypeField(index) {
    setForm((prev) => ({
      ...prev,
      planTypes: prev.planTypes.filter((_, i) => i !== index),
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.planId.trim() || !form.planName.trim()) {
      alert("Plan ID and Name are required");
      return;
    }

    try {
      if (isEditing) {
        const updated = await updateInsurancePlan(form.id, {
          planId: form.planId,
          planName: form.planName,
          planTypes: form.planTypes.filter((t) => t.typeName.trim()),
        });
        setPlans((prev) => prev.map((p) => (p.id === form.id ? updated : p)));
        setIsEditing(false);
      } else {
        const created = await createInsurancePlan({
          planId: form.planId,
          planName: form.planName,
          planTypes: form.planTypes.filter((t) => t.typeName.trim()),
        });
        setPlans((prev) => [...prev, created]);
      }

      setForm({
        id: null,
        planId: "",
        planName: "",
        planTypes: [{ typeName: "" }],
      });
    } catch (err) {
      console.error("Failed to save plan", err);
      alert(err.response?.data?.message || "Error saving insurance plan.");
    }
  }

  function handleEdit(plan) {
    setForm({
      id: plan.id,
      planId: plan.planId,
      planName: plan.planName,
      planTypes: plan.planTypes?.length ? plan.planTypes : [{ typeName: "" }],
    });
    setIsEditing(true);
  }

  async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this insurance plan?"))
      return;

    try {
      await deleteInsurancePlan(id);
      setPlans((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Failed to delete plan", err);
      alert("Error deleting insurance plan.");
    }
  }

  function handleCancel() {
    setForm({
      id: null,
      planId: "",
      planName: "",
      planTypes: [{ typeName: "" }],
    });
    setIsEditing(false);
  }

  return (
    <div>
      <h2 className="text-xl font-semibold mb-3">Insurance Plans</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 max-w-lg bg-gray-50 p-4 rounded shadow"
      >
        <div className="mb-3">
          <label className="block font-medium mb-1">Plan ID *</label>
          <input
            name="planId"
            type="text"
            value={form.planId}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>

        <div className="mb-3">
          <label className="block font-medium mb-1">Plan Name *</label>
          <input
            name="planName"
            type="text"
            value={form.planName}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2"
            required
          />
        </div>

        <div className="mb-3">
          <label className="block font-medium mb-1">Plan Types</label>
          {form.planTypes.map((type, idx) => (
            <div key={idx} className="flex space-x-2 mb-2">
              <input
                type="text"
                value={type.typeName}
                onChange={(e) => handleTypeChange(idx, e.target.value)}
                className="flex-1 border rounded px-3 py-2"
                placeholder="Type Name"
              />
              {form.planTypes.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeTypeField(idx)}
                  className="bg-red-500 text-white px-2 rounded"
                >
                  X
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addTypeField}
            className="text-blue-600 text-sm mt-1"
          >
            + Add Type
          </button>
        </div>

        <div className="flex space-x-2 mt-4">
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded"
          >
            {isEditing ? "Update" : "Add"}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-300 px-4 py-2 rounded"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <table className="min-w-full border rounded shadow">
        <thead className="bg-gray-100">
          <tr>
            <th className="border px-4 py-2">ID</th>
            <th className="border px-4 py-2">Plan ID</th>
            <th className="border px-4 py-2">Plan Name</th>
            <th className="border px-4 py-2">Types</th>
            <th className="border px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5} className="text-center p-4">
                Loading...
              </td>
            </tr>
          ) : plans.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center p-4">
                No plans found.
              </td>
            </tr>
          ) : (
            plans.map((plan) => (
              <tr key={plan.id}>
                <td className="border px-4 py-2">{plan.id}</td>
                <td className="border px-4 py-2">{plan.planId}</td>
                <td className="border px-4 py-2">{plan.planName}</td>
                <td className="border px-4 py-2">
                  {plan.planTypes?.map((t) => t.typeName).join(", ")}
                </td>
                <td className="border px-4 py-2 space-x-2 text-center">
                  <button
                    onClick={() => handleEdit(plan)}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(plan.id)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default InsurancePlanTab;
